"use client";

import React, { createContext, useContext, useCallback, useMemo, ReactNode } from "react";
import { useBrowserPreference } from "../hooks/useBrowserPreference";
import { useEffectivePass } from "../hooks/useEffectivePass";

export const CHOICES_VISIBILITY_PREFERENCE_KEY = "jcg:default-choices-hidden";

interface ChoicesVisibilityContextValue {
  globalChoicesHidden: boolean;
  toggleGlobalChoicesHidden: () => void;
}

const ChoicesVisibilityContext = createContext<ChoicesVisibilityContextValue | null>(null);

export const useChoicesVisibility = () => {
  return useContext(ChoicesVisibilityContext);
};

interface ChoicesVisibilityProviderProps {
  children: ReactNode;
}

export const ChoicesVisibilityProvider: React.FC<ChoicesVisibilityProviderProps> = ({ children }) => {
  const [storedChoicesHidden, setStoredChoicesHidden] = useBrowserPreference(
    CHOICES_VISIBILITY_PREFERENCE_KEY,
    false,
  );
  const { isPremiumOrAbove } = useEffectivePass();
  const globalChoicesHidden = isPremiumOrAbove && storedChoicesHidden;

  const toggleGlobalChoicesHidden = useCallback(() => {
    if (isPremiumOrAbove) setStoredChoicesHidden(!storedChoicesHidden);
  }, [isPremiumOrAbove, setStoredChoicesHidden, storedChoicesHidden]);

  const value = useMemo(() => ({
    globalChoicesHidden,
    toggleGlobalChoicesHidden,
  }), [globalChoicesHidden, toggleGlobalChoicesHidden]);

  return (
    <ChoicesVisibilityContext.Provider value={value}>
      {children}
    </ChoicesVisibilityContext.Provider>
  );
};
